import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Plane, Radar, Users } from "lucide-react";
import { api, ApiError, type Role } from "../lib/api";
import { AcceptInvite, MfaCodeStep, MfaSetup } from "./Mfa";

const ICONS: Record<string, React.ReactNode> = {
  planner: <Radar className="size-5" />,
  handler: <Users className="size-5" />,
  airline: <Plane className="size-5" />,
};

export function Login({
  demo,
  pending,
}: {
  demo: Array<{ id: string; name: string; role: Role; blurb: string }> | null;
  /** Password already accepted: waiting for the authenticator code, or for it to be set up. */
  pending: { stage: "mfa" | "setup"; email: string; name: string } | null;
}) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const done = async () => {
    await qc.invalidateQueries();
  };

  // A welcome email sends people to /?invite=<token> to choose a password.
  const [inviteToken, setInviteToken] = useState(() => new URLSearchParams(window.location.search).get("invite"));

  const restart = async () => {
    await api.logout().catch(() => {});
    setPassword("");
    await done();
  };

  return (
    <div className="grid min-h-full place-items-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <div className="grid size-11 place-items-center rounded-xl bg-brand text-brand-ink">
            <svg viewBox="0 0 32 32" className="size-7">
              <path d="M8 20h16M11 14h10M14 8h4" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <div className="text-[20px] font-bold tracking-tight">Vectro Allocate</div>
            <div className="text-[13px] text-ink-3">Stands, gates, counters and belts, planned in seconds</div>
          </div>
        </div>

        {inviteToken && (
          <AcceptInvite
            token={inviteToken}
            onDone={() => {
              window.history.replaceState(null, "", window.location.pathname);
              setInviteToken(null);
            }}
          />
        )}

        {!inviteToken && pending?.stage === "mfa" && <MfaCodeStep email={pending.email} onRestart={() => void restart()} />}

        {!inviteToken && pending?.stage === "setup" && (
          <div className="rounded-xl border border-line bg-panel p-4">
            <MfaSetup
              onDone={() => void done()}
              intro={
                <div>
                  <div className="text-[15px] font-bold">Set up two-step sign-in</div>
                  <p className="mt-1 text-[12.5px] text-ink-3">
                    {pending.name}, Vectro Allocate now asks for a code from an authenticator app when you sign in. This takes about a minute and is needed once.
                  </p>
                </div>
              }
            />
            <button className="mt-3 text-[12px] text-ink-3 underline" onClick={() => void restart()}>
              Sign in as someone else
            </button>
          </div>
        )}

        {!inviteToken && !pending && demo && (
          <div className="mb-4 rounded-xl border border-line bg-panel p-3">
            <div className="mb-2 text-[12px] font-semibold tracking-wide text-ink-3 uppercase">Try it as</div>
            <div className="space-y-2">
              {demo.map((d) => (
                <button
                  key={d.id}
                  disabled={busy !== null}
                  onClick={async () => {
                    setBusy(d.id);
                    try {
                      await api.demoLogin(d.id);
                      await done();
                    } finally {
                      setBusy(null);
                    }
                  }}
                  className="flex w-full items-center gap-3 rounded-lg border border-line p-2.5 text-left hover:border-ink-3 disabled:opacity-60"
                >
                  <div className="grid size-9 place-items-center rounded-lg bg-panel-2 text-ink-2">
                    {busy === d.id ? <Loader2 className="size-5 animate-spin" /> : ICONS[d.role]}
                  </div>
                  <div>
                    <div className="text-[13.5px] font-semibold">{d.name}</div>
                    <div className="text-[12px] text-ink-3">{d.blurb}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {!inviteToken && !pending && (
        <form
          className="space-y-3 rounded-xl border border-line bg-panel p-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            setBusy("form");
            try {
              await api.login(email, password);
              await done();
            } catch (err) {
              setError(err instanceof ApiError ? err.message : "Sign-in failed");
            } finally {
              setBusy(null);
            }
          }}
        >
          <label className="block text-[12px] font-semibold">
            Email
            <input
              type="email"
              autoComplete="username"
              required
              className="mt-1 w-full rounded-md border border-line bg-panel px-2.5 py-2 text-[14px]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="block text-[12px] font-semibold">
            Password
            <input
              type="password"
              autoComplete="current-password"
              required
              className="mt-1 w-full rounded-md border border-line bg-panel px-2.5 py-2 text-[14px]"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {error && <div className="text-[12.5px] text-bad">{error}</div>}
          <button disabled={busy !== null} className="w-full rounded-lg bg-ink py-2 text-[14px] font-bold text-panel disabled:opacity-60">
            {busy === "form" ? "Signing in…" : "Sign in"}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
